import React, { useContext, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Container,
  Button,
  Chip,
  Paper,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import { loginUser } from '../services/authService';
import { motion } from 'framer-motion';
import { LockOutlined as LockOutlinedIcon } from '@mui/icons-material';
import logo from '../assets/logo2.png';

function LoginPage() {
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, user, isLoading } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!isLoading && user) {
      navigate(location.state?.from?.pathname || '/', { replace: true });
    }
  }, [isLoading, location.state, navigate, user]);

  const handleChange = (e) => {
    setCredentials({ ...credentials, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const token = await loginUser(credentials);
      login(token);
      navigate('/');
    } catch (err) {
      setError(err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        py: { xs: 4, md: 6 },
        px: 2,
        background: '#F8FAFC',
      }}
    >
      <Container maxWidth="lg">
        <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <Paper
            elevation={0}
            sx={{
              overflow: 'hidden',
              borderRadius: { xs: 4, md: 5 },
              background: '#FFFFFF',
            }}
          >
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.08fr 0.92fr' } }}>
              <Box
                sx={{
                  p: { xs: 4, md: 6 },
                  background: '#F8FAFC',
                  borderBottom: { xs: '1px solid', md: 'none' },
                  borderRight: { xs: 'none', md: '1px solid' },
                  borderColor: 'divider',
                }}
              >
                <Chip
                  icon={<LockOutlinedIcon />}
                  label="Secure admin access"
                  color="secondary"
                  variant="outlined"
                  sx={{ mb: 3, bgcolor: '#FFFFFF' }}
                />
                <Box
                  component="img"
                  src={logo}
                  alt="MeGuide Consulting"
                  sx={{ width: 160, maxWidth: '100%', height: 'auto' }}
                />
                <Typography variant="h3" sx={{ mt: 4 }}>
                  Manage content, media, and requests from one polished workspace.
                </Typography>
                <Typography color="text.secondary" sx={{ mt: 2, maxWidth: 520, fontSize: '1.02rem', lineHeight: 1.75 }}>
                  A clean, fast workspace for publishing, updates, uploads, and operational review across the MeGuide platform.
                </Typography>

                
              </Box>

              <Box sx={{ p: { xs: 4, md: 6 }, display: 'flex', alignItems: 'center' }}>
                <Box sx={{ width: '100%', maxWidth: 420, mx: 'auto' }}>
                  <Typography variant="h4">Welcome back</Typography>
                  <Typography color="text.secondary" sx={{ mt: 1.25, mb: 4 }}>
                    Sign in to continue to the MeGuide admin workspace.
                  </Typography>

                  {error && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                      {error}
                    </Alert>
                  )}

                  <form onSubmit={handleSubmit}>
                    <TextField
                      label="Username"
                      name="username"
                      variant="outlined"
                      margin="normal"
                      fullWidth
                      required
                      value={credentials.username}
                      onChange={handleChange}
                    />
                    <TextField
                      label="Password"
                      name="password"
                      variant="outlined"
                      margin="normal"
                      fullWidth
                      required
                      type="password"
                      value={credentials.password}
                      onChange={handleChange}
                    />
                    <Box mt={3}>
                      <motion.div whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.99 }}>
                        <Button
                          type="submit"
                          variant="contained"
                          color="primary"
                          fullWidth
                          disabled={loading || isLoading}
                          sx={{ minHeight: 52 }}
                        >
                          {loading ? <CircularProgress size={24} color="inherit" /> : 'Access Dashboard'}
                        </Button>
                      </motion.div>
                    </Box>
                  </form>
                </Box>
              </Box>
            </Box>
          </Paper>
        </motion.div>
      </Container>
    </Box>
  );
}

export default LoginPage;
